import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";

interface KeyboardShortcutsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutItemProps {
  label: string;
  keyLabel: string;
}

const ShortcutItem: React.FC<ShortcutItemProps> = ({ label, keyLabel }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      border: "none",
    }}
  >
    <span
      style={{
        color: "rgba(255, 255, 255, 0.8)",
        fontSize: "14px",
      }}
    >
      {label}
    </span>
    <kbd
      style={{
        background: "rgba(255, 255, 255, 0.1)",
        border: "none",
        borderRadius: "6px",
        padding: "4px 8px",
        fontSize: "12px",
        color: "rgba(255, 255, 255, 0.9)",
        fontFamily: "monospace",
      }}
    >
      {keyLabel}
    </kbd>
  </div>
);

const CloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <button
    onClick={onClose}
    style={{
      position: "absolute",
      top: "16px",
      right: "16px",
      background: "none",
      border: "none",
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "20px",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = "rgba(255, 255, 255, 1)";
      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)";
      e.currentTarget.style.backgroundColor = "transparent";
    }}
  >
    {FaTimes({})}
  </button>
);

const shortcuts: ShortcutItemProps[] = [
  { label: "Start/Stop", keyLabel: "Space" },
  { label: "Mute/Unmute", keyLabel: "M" },
  { label: "Adjust BPM by 1", keyLabel: "↑ ↓" },
  { label: "Adjust BPM by 10", keyLabel: "Shift + ↑ ↓" },
  { label: "Adjust Beats per Bar", keyLabel: "Ctrl + ↑ ↓" },
  { label: "Adjust Note Value", keyLabel: "Ctrl + Shift + ↑ ↓" },
  { label: "Cycle Click Style", keyLabel: "X" },
  { label: "Toggle Stress Beat", keyLabel: "B" },
  { label: "Toggle Flash Dial", keyLabel: "D" },
  { label: "Toggle Flash Screen", keyLabel: "F" },
  { label: "Toggle Subdivisions", keyLabel: "S" },
  { label: "Toggle Zen Mode", keyLabel: "Z" },
  { label: "Toggle Timer", keyLabel: "T" },
  { label: "Toggle Progressive Tempo", keyLabel: "P" },
  { label: "Toggle Custom Song Structure", keyLabel: "C" },
  {
    label: "Reset to Bar 1",
    keyLabel: "R",
  },
  {
    label: "Toggle Loop",
    keyLabel: "L",
  },
  {
    label: "Next Bar",
    keyLabel: "→",
  },
  {
    label: "Previous Bar",
    keyLabel: "←",
  },
  { label: "Keyboard Shortcuts", keyLabel: "Tab" },
];

export const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  isOpen,
  onClose,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const tabShortcut = shortcuts.find((s) => s.keyLabel === "Tab");
  const otherShortcuts = shortcuts.filter((s) => s.keyLabel !== "Tab");

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Force a re-render cycle to ensure initial styles are applied
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backdropFilter: "blur(10px)",
        opacity: isAnimating ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--modal-bg)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "20px",
          border: "none",
          padding: "32px",
          maxWidth: "800px",
          width: "90%",
          position: "relative",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
          transform: isAnimating ? "translateY(0)" : "translateY(100px)",
          opacity: isAnimating ? 1 : 0,
          transition: "all 0.3s ease-in-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClose={onClose} />

        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "24px",
              fontWeight: "600",
              margin: "0 0 8px 0",
            }}
          >
            Keyboard Shortcuts
          </h2>
          <p
            style={{
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "14px",
              margin: 0,
            }}
          >
            Use these shortcuts to control the metronome
          </p>
        </div>

        {/* Shortcuts list */}
        <div
          style={{
            display: "flex",
            gap: "32px",
          }}
        >
          {/* Left column */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              flex: 1,
            }}
          >
            {otherShortcuts.slice(0, 10).map((shortcut, index) => (
              <ShortcutItem
                key={index}
                label={shortcut.label}
                keyLabel={shortcut.keyLabel}
              />
            ))}
          </div>

          {/* Right column */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              flex: 1,
            }}
          >
            {otherShortcuts.slice(10).map((shortcut, index) => (
              <ShortcutItem
                key={index + 8}
                label={shortcut.label}
                keyLabel={shortcut.keyLabel}
              />
            ))}
          </div>
        </div>

        {/* Full-width Keyboard Shortcuts (Tab) entry spanning two columns */}
        {tabShortcut && (
          <div
            style={{
              marginTop: "16px",
            }}
          >
            <ShortcutItem
              label={tabShortcut.label}
              keyLabel={tabShortcut.keyLabel}
            />
          </div>
        )}
      </div>
    </div>
  );
};
